import Axios from '../config/Axios';

/**
 * Retorna uma página da lista de clientes
 */
export const getCustomers = async (page = 1, orderBy = 'cust_nid', orderType = 'asc', search = '', nWlpnId = null) => {
  const oResponse = await Axios.get('/customer', {
    params: {
      page,
      orderBy,
      orderType,
      ...(!!search && { search }),
      ...(!!nWlpnId && { nWlpnId }),
    },
  });
  return oResponse.data;
};

/**
 * Cria cliente direto pelo admin e dispara criacao do banco tenant.
 */
export const createCustomer = async ({ oCustomer, oUser, template_id = null }) => {
  const { data: customerData } = await Axios.post('customer', {
    oCustomer,
    oUser,
    template_id
  });

  const { data: message } = await Axios.post('customer/create-database', {
    nCustId: customerData.nCustId
  });

  return { nCustId: customerData.nCustId, message };
};

/**
 * Envia convite de cadastro com modelo opcional selecionado pelo admin.
 */
export const preRegisterCustomer = async ({ cName, cEmail, template_id = null }) => {
  const oResponse = await Axios.post('customer/pre-register', {
    cName,
    cEmail,
    template_id
  });
  return oResponse.data;
};

/**
 * Lista modelos de base ativos que podem ser usados no cadastro de cliente.
 */
export const getCustomerTemplateOptions = async () => {
  const oResponse = await Axios.get('customer/templates');
  return oResponse.data;
};

export const auditCustomerTemplateReconciliation = async ({ templateId, targetCustomerIds, modules = ['operational_panel'] }) => {
  const oResponse = await Axios.post('customer/template-reconciliation/audit', {
    template_id: templateId,
    target_cust_nids: targetCustomerIds,
    modules,
  });
  return oResponse.data;
};

export const applyCustomerTemplateReconciliation = async ({ templateId, targetCustomerIds, modules, fingerprint }) => {
  const oResponse = await Axios.post('customer/template-reconciliation/apply', {
    template_id: templateId,
    target_cust_nids: targetCustomerIds,
    modules,
    fingerprint,
  });
  return oResponse.data;
};

/**
 * Busca a ultima importacao de modelo registrada para o cliente.
 */
export const getCustomerTemplateImport = async nCustId => {
  const oResponse = await Axios.get(`customer/${nCustId}/template-import`);
  return oResponse.data;
};

/**
 * Gera um resumo somente-leitura entre os IDs importados do modelo e o cliente destino.
 */
export const getCustomerTemplateImportComparison = async nImportId => {
  const oResponse = await Axios.get(`customer/template-imports/${nImportId}/comparison`);
  return oResponse.data;
};

/**
 * Agenda o reprocessamento de uma importacao pendente ou falha.
 */
export const reprocessCustomerTemplateImport = async nImportId => {
  const oResponse = await Axios.post(`customer/template-imports/${nImportId}/reprocess`);
  return oResponse.data;
};

/**
 * Agenda a atualizacao de schema somente para o tenant destino da importacao.
 */
export const migrateCustomerTemplateTenant = async nImportId => {
  const oResponse = await Axios.post(`customer/template-imports/${nImportId}/migrate-tenant`);
  return oResponse.data;
};

export const validateCustomerTemplateTenantMigrations = async nImportId => Axios.post(`customer/template-imports/${nImportId}/validate-tenant-migrations`);
export const reconcileCustomerTemplateTenantMigrations = async (nImportId, migrations) => Axios.post(`customer/template-imports/${nImportId}/reconcile-tenant-migrations`, { migrations });

/**
 * Atualiza o inventario contratual e de conteudo de um modelo antes da recuperacao parcial.
 */
export const refreshCustomerTemplateModules = async nTemplateId => {
  const oResponse = await Axios.post('customer/templates/' + nTemplateId + '/refresh-modules');
  return oResponse.data;
};

/**
 * Agenda somente os modulos escolhidos, preservando o mapa de IDs ja importado.
 */
export const reprocessCustomerTemplateModules = async (nImportId, aModules) => {
  const oResponse = await Axios.post('customer/template-imports/' + nImportId + '/reprocess-modules', {
    modules: aModules,
  });
  return oResponse.data;
};

/**
 * Marca importacao running sem progresso como falha para liberar reprocessamento.
 */
export const unlockCustomerTemplateImport = async nImportId => {
  const oResponse = await Axios.post(`customer/template-imports/${nImportId}/unlock`);
  return oResponse.data;
};

export const disableCustomer = async nCustId => {
  const oResponse = await Axios.put(`customer/disable/${nCustId}`);
  return oResponse.data;
};

/**
 * @returns {Promise<[{ value: number, label: string }]>}
 */
export const getCustomerOptionsList = async () => {
  const oResponse = await Axios.get('customer/options');
  return oResponse.data;
};

export const getCustomerPlan = async nCustId => {
  const oResponse = await Axios.get(`customer/plan/${nCustId}`);
  return oResponse.data;
};

export const getCustomerModuleVersions = async nCustId => {
  const oResponse = await Axios.get(`customer/module-versions/${nCustId}`);
  return oResponse.data;
};

export const getCustomerTelemetry = async (nCustId, nWindowDays = 30) => {
  const oResponse = await Axios.get(`customer/${nCustId}/telemetry`, {
    params: {
      window_days: nWindowDays,
    },
  });
  return oResponse.data;
};

export const refreshCustomerTelemetry = async (nCustId, nWindowDays = 30) => {
  const oResponse = await Axios.post(`customer/${nCustId}/telemetry/refresh`, null, {
    params: {
      window_days: nWindowDays,
    },
  });
  return oResponse.data;
};

export const exportCustomerTelemetry = async () => {
  const oResponse = await Axios.get('customer/telemetry/export', {
    responseType: 'blob',
  });
  return oResponse;
};

export const getPlanConfig = async () => {
  const oResponse = await Axios.get('/system/plan-config');
  return oResponse.data;
};

export const updateCustomerPlan = async (nCustId, oBody) => {
  const oResponse = await Axios.put(`/customer/subscription-change/${nCustId}`, oBody);
  return oResponse.data;
};

export const updateCustomerModuleVersions = async (nCustId, oBody) => {
  const oResponse = await Axios.put(`/customer/module-versions/${nCustId}`, oBody);
  return oResponse.data;
};

export const updatePlanEnd = async (nCustId, oBody) => {
  const oResponse = await Axios.put(`/customer/plan-end/${nCustId}`, oBody);
};

export const createCustomerPlan = async (nCustId, oBody) => {
  const oResponse = await Axios.post(`/customer/signup/${nCustId}`, oBody);
  return oResponse.data;
};

export const getExtract = async nCustId => {
  const oResponse = await Axios.get(`/customer/extract/${nCustId}`);
  return oResponse.data;
};
